import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './Promotions.css';

const Promotions = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Placeholder promotions data
  const allPromotions = [
    {
      id: 'PROM001',
      name: 'Summer Sale',
      code: 'SUMMER2024',
      type: 'Discount',
      discount: '15%',
      routes: 'All Routes',
      startDate: '2024-06-01',
      endDate: '2024-08-31',
      status: 'Active'
    },
    {
      id: 'PROM002',
      name: 'Lagos to London Deal',
      code: 'LAGLON50',
      type: 'Deal',
      discount: '₦50,000',
      routes: 'Lagos → London',
      startDate: '2024-12-01',
      endDate: '2024-12-31',
      status: 'Active'
    },
    {
      id: 'PROM003',
      name: 'Flash Sale - Domestic',
      code: 'FLASH24',
      type: 'Flash Sale',
      discount: '25%',
      routes: 'All Domestic',
      startDate: '2024-12-20',
      endDate: '2024-12-25',
      status: 'Active'
    },
    {
      id: 'PROM004',
      name: 'New Year Special',
      code: 'NEWYEAR2025',
      type: 'Discount',
      discount: '20%',
      routes: 'All Routes',
      startDate: '2025-01-01',
      endDate: '2025-01-31',
      status: 'Inactive'
    },
    {
      id: 'PROM005',
      name: 'Business Class Upgrade',
      code: 'BIZUP30',
      type: 'Deal',
      discount: '₦30,000',
      routes: 'International Routes',
      startDate: '2024-11-01',
      endDate: '2024-12-31',
      status: 'Active'
    },
    {
      id: 'PROM006',
      name: 'Weekend Getaway',
      code: 'WEEKEND15',
      type: 'Discount',
      discount: '15%',
      routes: 'Domestic Routes',
      startDate: '2024-10-01',
      endDate: '2024-10-31',
      status: 'Inactive'
    },
    {
      id: 'PROM007',
      name: 'Black Friday Sale',
      code: 'BLACKFRIDAY',
      type: 'Flash Sale',
      discount: '30%',
      routes: 'All Routes',
      startDate: '2024-11-25',
      endDate: '2024-11-29',
      status: 'Inactive'
    },
    {
      id: 'PROM008',
      name: 'Student Discount',
      code: 'STUDENT10',
      type: 'Discount',
      discount: '10%',
      routes: 'All Routes',
      startDate: '2024-09-01',
      endDate: '2025-08-31',
      status: 'Active'
    },
    {
      id: 'PROM009',
      name: 'Early Bird Special',
      code: 'EARLY20',
      type: 'Discount',
      discount: '20%',
      routes: 'All Routes',
      startDate: '2024-12-01',
      endDate: '2025-02-28',
      status: 'Active'
    },
    {
      id: 'PROM010',
      name: 'Group Booking Deal',
      code: 'GROUP25',
      type: 'Deal',
      discount: '₦25,000',
      routes: 'All Routes',
      startDate: '2024-11-01',
      endDate: '2024-12-31',
      status: 'Active'
    }
  ];

  // Filter promotions based on search and filters
  const filteredPromotions = allPromotions.filter(promotion => {
    const matchesSearch = 
      promotion.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      promotion.code.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      promotion.status.toLowerCase() === statusFilter.toLowerCase();
    
    const matchesType = typeFilter === 'all' || 
      promotion.type.toLowerCase().replace(' ', '-') === typeFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredPromotions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedPromotions = filteredPromotions.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleAddPromotion = () => {
    navigate('/admin/promotions/add');
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting promotions to CSV...');
  };

  const handleViewPromotion = (promotionId) => {
    // Placeholder for view promotion
    console.log('View promotion:', promotionId);
  };

  const handleEditPromotion = (promotionId) => {
    // Placeholder for edit promotion
    console.log('Edit promotion:', promotionId);
  };

  const handleTogglePromotion = (promotionId) => {
    // Placeholder for disable/enable promotion
    console.log('Toggle promotion:', promotionId);
  };

  return (
    <AdminLayout pageTitle="Promotions">
      <div className="admin-promotions-page">
        {/* Page Title */}
        <div className="admin-promotions-header">
          <h1 className="admin-promotions-title">Promotions</h1>
          <p className="admin-promotions-subtitle">Manage discounts and deals</p>
        </div>

        {/* Top Control Bar */}
        <div className="admin-promotions-controls">
          <div className="admin-controls-left">
            <button
              className="admin-add-promotion-button"
              onClick={handleAddPromotion}
            >
              + Add Promotion
            </button>
            
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by promotion name or code..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              className="admin-filter-select"
              value={typeFilter}
              onChange={handleTypeFilterChange}
            >
              <option value="all">All Types</option>
              <option value="discount">Discount</option>
              <option value="deal">Deal</option>
              <option value="flash-sale">Flash Sale</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <button className="admin-export-button" onClick={handleExport}>
              <span className="admin-export-icon">📥</span>
              Export CSV
            </button>
          </div>
        </div>

        {/* Promotions Table */}
        {paginatedPromotions.length > 0 ? (
          <>
            <div className="admin-promotions-table-wrapper">
              {/* Desktop Table */}
              <table className="admin-promotions-table">
                <thead>
                  <tr>
                    <th>Promotion Name</th>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Discount</th>
                    <th>Applicable Routes</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPromotions.map((promotion) => (
                    <tr key={promotion.id} className="admin-promotions-table-row">
                      <td className="admin-table-cell admin-table-name">{promotion.name}</td>
                      <td className="admin-table-cell admin-table-code">{promotion.code}</td>
                      <td className="admin-table-cell">
                        <span className={`admin-type-badge admin-type-${promotion.type.toLowerCase().replace(' ', '-')}`}>
                          {promotion.type}
                        </span>
                      </td>
                      <td className="admin-table-cell admin-table-discount">{promotion.discount}</td>
                      <td className="admin-table-cell">{promotion.routes}</td>
                      <td className="admin-table-cell">
                        <span className={`admin-status-badge admin-status-${promotion.status.toLowerCase()}`}>
                          {promotion.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-actions-menu">
                          <button
                            className="admin-action-button"
                            onClick={() => handleViewPromotion(promotion.id)}
                          >
                            View
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleEditPromotion(promotion.id)}
                          >
                            Edit
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleTogglePromotion(promotion.id)}
                          >
                            {promotion.status === 'Active' ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="admin-promotions-cards">
                {paginatedPromotions.map((promotion) => (
                  <div key={promotion.id} className="admin-promotion-card">
                    <div className="admin-promotion-card-header">
                      <div className="admin-promotion-card-name">{promotion.name}</div>
                      <span className={`admin-status-badge admin-status-${promotion.status.toLowerCase()}`}>
                        {promotion.status}
                      </span>
                    </div>
                    <div className="admin-promotion-card-body">
                      <div className="admin-promotion-card-row">
                        <span className="admin-promotion-card-label">Code:</span>
                        <span className="admin-promotion-card-value admin-promotion-card-code">{promotion.code}</span>
                      </div>
                      <div className="admin-promotion-card-row">
                        <span className="admin-promotion-card-label">Type:</span>
                        <span className="admin-promotion-card-value">
                          <span className={`admin-type-badge admin-type-${promotion.type.toLowerCase().replace(' ', '-')}`}>
                            {promotion.type}
                          </span>
                        </span>
                      </div>
                      <div className="admin-promotion-card-row">
                        <span className="admin-promotion-card-label">Discount:</span>
                        <span className="admin-promotion-card-value admin-promotion-card-discount">{promotion.discount}</span>
                      </div>
                      <div className="admin-promotion-card-row">
                        <span className="admin-promotion-card-label">Routes:</span>
                        <span className="admin-promotion-card-value">{promotion.routes}</span>
                      </div>
                      <div className="admin-promotion-card-row">
                        <span className="admin-promotion-card-label">Start:</span>
                        <span className="admin-promotion-card-value">{promotion.startDate}</span>
                      </div>
                      <div className="admin-promotion-card-row">
                        <span className="admin-promotion-card-label">End:</span>
                        <span className="admin-promotion-card-value">{promotion.endDate}</span>
                      </div>
                    </div>
                    <div className="admin-promotion-card-actions">
                      <select
                        className="admin-mobile-actions-select"
                        onChange={(e) => {
                          const action = e.target.value;
                          if (action === 'view') handleViewPromotion(promotion.id);
                          else if (action === 'edit') handleEditPromotion(promotion.id);
                          else if (action === 'toggle') handleTogglePromotion(promotion.id);
                          e.target.value = '';
                        }}
                      >
                        <option value="">Actions...</option>
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                        <option value="toggle">{promotion.status === 'Active' ? 'Disable' : 'Enable'}</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredPromotions.length)} of {filteredPromotions.length} promotions
              </div>
              <div className="admin-pagination-controls">
                <select
                  className="admin-rows-select"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="admin-page-numbers">
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="admin-page-ellipsis">...</span>}
                          <button
                            className={`admin-page-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="admin-empty-state">
            <div className="admin-empty-icon">🎁</div>
            <h3 className="admin-empty-title">No promotions found</h3>
            <p className="admin-empty-text">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'Get started by adding your first promotion'}
            </p>
            <button
              className="admin-empty-action-button"
              onClick={handleAddPromotion}
            >
              + Add Promotion
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Promotions;

